import type { ListedPiece } from "./catalog-types";
import { featuredCount } from "./catalog-scope";
import { entryTotal, packArrangementLabel, type PackForm } from "./catalog-pack";

/**
 * Mutations of the catalog under management (handoff §7.5 and §7.6), as pure functions.
 *
 * The confirmation message is produced here rather than in the component because it
 * **describes what happened**, and that is the same computation that decides the mutation.
 * Splitting them would let the screen confirm something other than what it wrote.
 */

/** Handoff §7.6: the prototype caps featured pieces at eight. */
export const FEATURED_LIMIT = 8;

export type MutationResult = {
  pieces: ListedPiece[];
  /** Toast copy. Null when nothing happened and nothing should be said. */
  message: string | null;
  /** True when the operation was refused. */
  isRejected: boolean;
  /**
   * For an operation over many pieces, which ones it changes, and how to say it of a **subset**.
   *
   * The write can land in part: the backend refuses one piece of a batch (it already has gifts,
   * it sits in someone's cart) and the others go through. The confirmation must then describe
   * what happened, not what was asked — so the writer asks for the sentence again with the count
   * that landed. Absent on a single-piece operation, which lands whole or not at all.
   */
  affectedIds?: readonly string[];
  messageFor?: (landed: number) => string;
};

function replace(
  pieces: readonly ListedPiece[],
  id: string,
  patch: Partial<ListedPiece>,
): ListedPiece[] {
  return pieces.map((piece) => (piece.id === id ? { ...piece, ...patch } : piece));
}

/**
 * Writes the arrangement and ensures the piece is in the list.
 *
 * Configuring the pack of an outside piece **is** adding it: the prototype opens the same
 * dialog either way, and leaving it with an arrangement but no membership would make the
 * choice a no-op.
 */
export function applyPack(
  pieces: readonly ListedPiece[],
  id: string,
  form: PackForm,
): MutationResult {
  const piece = pieces.find((entry) => entry.id === id);
  if (piece === undefined) return { pieces: [...pieces], message: null, isRejected: true };

  const isNew = !piece.inList;
  const arrangement = packArrangementLabel(form);

  return {
    pieces: replace(pieces, id, { inList: true, total: form.total, pack: form.pack }),
    message: isNew
      ? `${piece.name} entrou na sua lista, com ${arrangement}`
      : `${arrangement} salvos`,
    isRejected: false,
  };
}

/**
 * Takes the piece out of the list.
 *
 * Featured status falls with it, since a piece outside the list has no showcase to occupy.
 * The message says what is **not** lost, because that is the question this action raises.
 */
export function removePiece(pieces: readonly ListedPiece[], id: string): MutationResult {
  const piece = pieces.find((entry) => entry.id === id);
  if (piece === undefined) return { pieces: [...pieces], message: null, isRejected: true };

  return {
    pieces: replace(pieces, id, { inList: false, featured: false }),
    message: `${piece.name} saiu da sua lista. Os presentes já registrados seguem garantidos.`,
    isRejected: false,
  };
}

/**
 * Toggles featured status, respecting the limit.
 *
 * At the limit the operation is **refused** and the message says what to do next: silently
 * dropping the oldest featured piece would undo a choice the person made on purpose.
 */
export function toggleFeatured(pieces: readonly ListedPiece[], id: string): MutationResult {
  const piece = pieces.find((entry) => entry.id === id);
  if (piece === undefined) return { pieces: [...pieces], message: null, isRejected: true };

  if (!piece.featured && featuredCount(pieces) >= FEATURED_LIMIT) {
    return {
      pieces: [...pieces],
      message: `Você já tem ${FEATURED_LIMIT} destaques. Retire um para destacar esta peça.`,
      isRejected: true,
    };
  }

  return {
    pieces: replace(pieces, id, { featured: !piece.featured }),
    message: piece.featured ? "Destaque retirado" : "Peça destacada na sua lista",
    isRejected: false,
  };
}

export type BulkAction = "add" | "remove";

/**
 * Applies the bulk action to the marked pieces.
 *
 * Counts **only what changes**, so asking to add ten pieces of which eight are already in
 * adds two and says two. When nothing changes the operation does not run and the message
 * says so (criterion 17.17).
 */
export function applyBulk(
  pieces: readonly ListedPiece[],
  ids: ReadonlySet<string>,
  action: BulkAction,
): MutationResult {
  const isAdding = action === "add";

  const affected = pieces.filter(
    (piece) => ids.has(piece.id) && (isAdding ? !piece.inList : piece.inList),
  );

  if (affected.length === 0) {
    return { pieces: [...pieces], message: "Nada a mudar nessa seleção.", isRejected: true };
  }

  const affectedIds = new Set(affected.map((piece) => piece.id));
  const next = pieces.map((piece) =>
    affectedIds.has(piece.id)
      ? // Leaving the list clears featured too, as in the individual removal.
        {
          ...piece,
          inList: isAdding,
          featured: isAdding ? piece.featured : false,
          // Entering the list asks for at least one piece (one pack): never zero (`entryTotal`).
          total: isAdding ? entryTotal(piece) : piece.total,
        }
      : piece,
  );

  const say = (count: number) => {
    const noun = count === 1 ? "peça" : "peças";

    return isAdding
      ? `${count} ${noun} na sua lista. Configure o pacote de cada uma quando quiser.`
      : `${count} ${noun} fora da sua lista.`;
  };

  return {
    pieces: next,
    message: say(affected.length),
    isRejected: false,
    affectedIds: affected.map((piece) => piece.id),
    messageFor: say,
  };
}

/** Summary shown on the bulk selection bar. */
export function bulkSelectionLabel(count: number): string {
  return `${count} ${count === 1 ? "peça selecionada" : "peças selecionadas"}`;
}

/** How many marked pieces each action would touch, which decides if the button shows. */
export function bulkActionCounts(
  pieces: readonly ListedPiece[],
  ids: ReadonlySet<string>,
): { add: number; remove: number } {
  const selected = pieces.filter((piece) => ids.has(piece.id));

  return {
    add: selected.filter((piece) => !piece.inList).length,
    remove: selected.filter((piece) => piece.inList).length,
  };
}
